/**
 * The non-card ways to pay a basket — a session bundle (pass) the buyer owns,
 * or their membership's credits/benefits — as both basket checkouts offer
 * them: the widget's full-page checkout and the webcomponents overlay Website
 * v2 checks out through. Which bundles qualify, what each lookup is asked,
 * what is sent to confirm the order and how the total reads all live here, so
 * the two cannot drift.
 *
 * Both lookups are keyed to the SIGNED-IN account's email, never the buyer
 * form field: credits and bundles may only be spent from the authenticated
 * customer's account.
 */

/** A bundle the buyer owns that can pay for this course
 *  (`GET /widget/courses/:courseId/user-applicable-bundles`). */
export type UserApplicableBundle = {
  id: string;
  name: string;
  totalSessions: number;
  usedSessions: number;
  remainingSessions: number;
  expiredDate: string;
};

/** What the buyer's membership covers in this basket
 *  (`GET /widget/app/membership/resolve`). */
export type MembershipResolution = {
  subscriptionId: string;
  type: "credit" | "unlimited" | "frequency";
  creditsRemaining: number;
  coveredItemIndices: number[];
  planName: string;
  benefits?: Array<{
    benefitId: string;
    type: "credit" | "unlimited" | "frequency";
    usagesPerFrequency?: number;
    frequencyPeriod?: "week" | "month" | "days";
    frequencyDays?: number;
    usagesInWindow?: number;
  }>;
  benefitUsageMap?: Record<string, string>;
};

/** FinalStep's payment-method radio. */
export type CheckoutPaymentOption = "card" | "stripe" | "credits" | "bundle";

type PaymentOptionItem = {
  type: string;
  courseId?: string | null;
  sessionId?: string | null;
  quantity?: number;
  ticketSelections?: Array<{ quantity?: number }> | null;
};

/** How many tickets the basket books (a bundle spends one session each). */
export function basketTicketCount(items: PaymentOptionItem[]): number {
  return items.reduce((sum, item) => {
    if (item.ticketSelections && item.ticketSelections.length > 0) {
      return (
        sum +
        item.ticketSelections.reduce((tSum, ts) => tSum + (ts.quantity || 1), 0)
      );
    }
    return sum + (item.quantity || 1);
  }, 0);
}

/**
 * The bundles that can pay for this basket. All-or-nothing: only a basket of
 * bookings alone, and only a bundle with enough sessions left for every
 * ticket in it.
 */
export function eligibleBundlesFor<B extends { remainingSessions: number }>(
  items: PaymentOptionItem[],
  bundles: B[],
): B[] {
  if (items.some((item) => item.type !== "booking")) return [];
  const tickets = basketTicketCount(items);
  return bundles.filter((b) => b.remainingSessions >= tickets);
}

/** The course the bundle lookup is asked about (the first booking's), or
 *  null when the basket has no booking to pay with one. */
export function bundleLookupCourseId(items: PaymentOptionItem[]): string | null {
  return items.find((item) => item.type === "booking")?.courseId || null;
}

/** The `items` the membership lookup is asked about: every booking. */
export function membershipResolveItems(items: PaymentOptionItem[]) {
  return items
    .filter((item) => item.type === "booking")
    .map((item) => ({ courseId: item.courseId, sessionId: item.sessionId }));
}

/**
 * What the order is redeemed against. A request basket hides every way of
 * paying, so a pick left over from before the basket became a request one
 * (e.g. the paid item was removed) is never sent.
 */
export function resolveRedemption({
  isRequestToBook,
  selectedPaymentBundleId,
  selectedMembershipId,
}: {
  isRequestToBook: boolean;
  selectedPaymentBundleId: string | null | undefined;
  selectedMembershipId: string | null | undefined;
}): { paymentBundleId: string | null; paymentMembershipId: string | null } {
  return {
    paymentBundleId: isRequestToBook ? null : selectedPaymentBundleId || null,
    paymentMembershipId: isRequestToBook ? null : selectedMembershipId || null,
  };
}

/** Why the chosen payment method can't be submitted yet, or null. */
export function paymentOptionError({
  selectedPaymentOption,
  selectedPaymentBundleId,
  selectedMembershipId,
}: {
  selectedPaymentOption: CheckoutPaymentOption;
  selectedPaymentBundleId: string | null | undefined;
  selectedMembershipId: string | null | undefined;
}): string | null {
  if (selectedPaymentOption === "bundle" && !selectedPaymentBundleId) {
    return "Please select a bundle to use for payment.";
  }
  if (selectedPaymentOption === "credits" && !selectedMembershipId) {
    return "Please select a membership to use for payment.";
  }
  return null;
}

/** The Total the summary shows for a redeemed order ("Free (Bundle)"), or
 *  null for one that is paid (show the amount). */
export function redemptionTotalLabel({
  isRequestToBook,
  selectedPaymentBundleId,
  selectedMembershipId,
}: {
  isRequestToBook: boolean;
  selectedPaymentBundleId: string | null | undefined;
  selectedMembershipId: string | null | undefined;
}): string | null {
  if (isRequestToBook) return null;
  if (selectedPaymentBundleId) return "Free (Bundle)";
  if (selectedMembershipId) return "Free (Membership)";
  return null;
}

/**
 * The `/widget/app/confirmFreeBasketOrder` body for an order paid with a
 * bundle or a membership. Stripe is skipped entirely: the server creates the
 * basket order from these items, checks availability, persists the customer
 * fields, redeems any codes and spends the bundle sessions / membership
 * credits.
 */
export function redemptionConfirmBody<TItem, TBuyer>({
  items,
  buyer,
  customerFieldAnswers,
  discountCodes,
  sourceInfo,
  affiliateRef,
  paymentBundleId,
  paymentMembershipId,
}: {
  items: TItem[];
  buyer: TBuyer;
  customerFieldAnswers: unknown;
  discountCodes: string[] | undefined;
  sourceInfo: unknown;
  affiliateRef?: unknown;
  paymentBundleId: string | null;
  paymentMembershipId: string | null;
}) {
  return {
    items,
    buyer,
    customerFieldAnswers,
    discountCodes,
    source: "widget" as const,
    sourceInfo,
    affiliateRef,
    ...(paymentBundleId
      ? { bundleId: paymentBundleId }
      : { membershipSubscriptionId: paymentMembershipId ?? undefined }),
  };
}
